// src/main.jsx
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import App from '@/App.jsx';
import '@/index.css';
import ErrorBoundary from '@/safety/ErrorBoundary.jsx';
import { flushOutbox } from '@/api/r2';
import { generateAndSaveProgressSnapshot } from '@/lib/progress-snapshot'; // ⬅️ NEW

// ✅ PATCH iOS: evita crash per spam di history.replaceState
(function guardIOSReplaceState() {
  if (typeof window === "undefined" || !window.history) return;

  const origReplace = window.history.replaceState.bind(window.history);
  const origPush = window.history.pushState.bind(window.history);

  let lastHref = null;
  let lastTs = 0;

  function safeCall(origFn, args) {
    try {
      const urlArg = args[2];
      const nextHref =
        urlArg == null
          ? window.location.href
          : new URL(urlArg, window.location.href).href;

      const now = Date.now();

      // evita chiamate duplicate a raffica
      if (nextHref === lastHref && now - lastTs < 150) return;

      lastHref = nextHref;
      lastTs = now;

      return origFn(...args);
    } catch (e) {
      const msg = String(e && e.message ? e.message : e);
      if (
        msg.includes("replaceState()") &&
        msg.includes("more than 100 times")
      ) {
        console.warn("[history] replaceState rate limit ignorato");
        return;
      }
      throw e;
    }
  }

  window.history.replaceState = function (...args) {
    return safeCall(origReplace, args);
  };

  window.history.pushState = function (...args) {
    return safeCall(origPush, args);
  };
})();

// ✅ DEBUG: espongo le API in console
import * as Entities from '@/api/entities';

// --------------------
// ✅ RB DEBUG LOGGER (persistente)
// --------------------
const RB_DEBUG_KEY = 'rb_debug_log_v1';

function rbLog(event, extra = {}) {
  try {
    if (typeof window === 'undefined') return;
    const entry = {
      ts: new Date().toISOString(),
      event,
      path: window.location?.pathname || '',
      origin: window.location?.origin || '',
      navType: (performance.getEntriesByType?.('navigation')?.[0]?.type) || 'na',
      displayMode: (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ? 'standalone' : 'browser',
      hasSW: !!navigator.serviceWorker,
      hasController: !!navigator.serviceWorker?.controller,
      ...extra,
    };
    const raw = window.localStorage?.getItem(RB_DEBUG_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    arr.push(entry);
    // ring buffer max 120
    while (arr.length > 120) arr.shift();
    window.localStorage?.setItem(RB_DEBUG_KEY, JSON.stringify(arr));
  } catch {
    // no-op
  }
}

// Log globali utili in console
window.addEventListener('error', (e) => {
  console.error('[Global error]', e.error || e.message || e);
  rbLog('window_error', { msg: String(e?.message || e) });
});
window.addEventListener('unhandledrejection', (e) => {
  console.error('[Unhandled rejection]', e.reason);
  rbLog('unhandledrejection', { reason: String(e?.reason || e) });
});

// ✅ DEBUG: metto Entities su window e un paio di helper
if (typeof window !== 'undefined') {
  window.Entities = Entities;
  window.rb = window.rb || {};
  window.rb.dumpProgress = async () => {
    try {
      const u = await Entities.User.me();
      const tries = [
        { created_by: u.email },
        { created_by: u.id || u.uid },
        { user_id: u.id || u.uid || u.email },
      ];
      let all = [];
      for (const q of tries) {
        try {
          const res = await Entities.UserProgress.filter(q);
          if (Array.isArray(res) && res.length) { all = res; break; }
        } catch {}
      }
      console.table(all.map(p => ({
        lesson_id: p.lesson_id,
        lesson_completed: !!(p.lesson_completed || p.completed || p.is_completed),
        exercise_completed: !!p.exercise_completed,
        created_by: p.created_by || p.user_id,
      })));
      return all;
    } catch (err) {
      console.error('[rb.dumpProgress] error:', err);
      return [];
    }
  };

  window.rb.dumpLessons = async () => {
    try {
      const ls = await Entities.Lesson.list('order');
      console.table((ls || []).map(l => ({
        id: l.id, order: l.order, title: l.title,
        has_exercise: !!(l.exercise_instructions || l.exercise_question),
      })));
      return ls;
    } catch (err) {
      console.error('[rb.dumpLessons] error:', err);
      return [];
    }
  };

  // helper: stampa gli ultimi log SW/setup
  window.rb.dumpDebugLog = () => {
    try {
      const raw = localStorage.getItem(RB_DEBUG_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      console.table(arr.slice(-30));
      return arr;
    } catch {
      return [];
    }
  };
}

async function syncPushSubscription(reg) {
  try {
    const ready = reg || (await navigator.serviceWorker.ready);
    const sub = await ready.pushManager.getSubscription();
    if (!sub) {
      console.log('[push] nessuna subscription da sincronizzare');
      return;
    }

    let userId = 'anon';
    try {
      const u = await Entities.User.me();
      userId = u.id || u.uid || u.email || 'anon';
    } catch {}

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Rome';

    await fetch('/api/push-subscribe', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        userId,
        subscription: sub,
        tz,
        ua: navigator.userAgent
      })
    });

    console.log('[push] subscription sincronizzata');
  } catch (err) {
    console.error('[push] sync error:', err);
  }
}

function isSetupFlowPath(pathname) {
  const p = String(pathname || '');
  return p.startsWith('/setup') || p.startsWith('/tutorial');
}

function startPendingReloadWatcher() {
  if (typeof window === 'undefined') return;
  let started = false;
  if (started) return;
  started = true;

  // controlla ogni 800ms: se c'è un reload pending e NON siamo nel setup flow, ricarica
  const t = setInterval(() => {
    try {
      const pending = sessionStorage.getItem('rb_sw_reload_pending') === '1';
      if (!pending) return;

      const p = window.location?.pathname || '';
      if (isSetupFlowPath(p)) return;

      sessionStorage.removeItem('rb_sw_reload_pending');
      rbLog('sw_reload_after_setupflow', { path: p });
      console.log('[SW] pending reload → reload now (outside setup flow)');
      window.location.reload();
    } catch {
      // no-op
    }
  }, 800);

  // nel caso la pagina venga distrutta
  window.addEventListener('beforeunload', () => clearInterval(t));
}

function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', async () => {
    try {
      rbLog('boot_load');

      // ✅ prova a chiedere storage persistente (aiuta su PWA)
      try {
        if (navigator.storage?.persist) {
          const persisted = await navigator.storage.persist();
          rbLog('storage_persist', { persisted: !!persisted });
          console.log('[storage] persist:', persisted);
        }
      } catch (e) {
        rbLog('storage_persist_error', { msg: String(e) });
      }

      startPendingReloadWatcher();

      const reg = await navigator.serviceWorker.register('/sw.js?v=2025-10-09-2', { scope: '/' });
      console.log('[SW] registered:', reg.scope);
      rbLog('sw_registered', { scope: reg.scope });

      syncPushSubscription(reg).catch(() => {});

      // ✅ FIX DEFINITIVO:
      // Non fare reload immediato su controllerchange se sei nel setup/tutorial.
      // (su Android PWA questo resetta l’app e perdi lo state)
      let reloaded = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        rbLog('sw_controllerchange', {
          pathNow: window.location?.pathname || '',
          hadController: !!navigator.serviceWorker?.controller
        });

        const p = window.location?.pathname || '';
        if (isSetupFlowPath(p)) {
          console.log('[SW] controllerchange during setup flow → defer reload');
          sessionStorage.setItem('rb_sw_reload_pending', '1');
          return;
        }

        // fuori dal setup flow puoi anche ricaricare (opzionale)
        if (reloaded) return;
        reloaded = true;
        console.log('[SW] controllerchange → reload (outside setup flow)');
        window.location.reload();
      });

      reg.addEventListener?.('updatefound', () => {
        console.log('[SW] update found]');
        rbLog('sw_updatefound');
      });
    } catch (err) {
      console.error('[SW] register error:', err);
      rbLog('sw_register_error', { msg: String(err) });
    }
  });
}

function Boot() {
  useEffect(() => {
    try {
      flushOutbox();
      rbLog('flushOutbox_ok');
    } catch (err) {
      console.error('flushOutbox failed:', err);
      rbLog('flushOutbox_error', { msg: String(err) });
    }
  }, []);

  useEffect(() => {
    registerServiceWorker();
  }, []);

  useEffect(() => {
    const onCheckinSaved = () => {
      generateAndSaveProgressSnapshot().catch((e) => {
        console.warn('[progress-snapshot] failed/queued', e);
        rbLog('progress_snapshot_failed', { msg: String(e) });
      });
    };
    window.addEventListener('rb:checkin-saved', onCheckinSaved);
    return () => window.removeEventListener('rb:checkin-saved', onCheckinSaved);
  }, []);

  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

const rootEl = document.getElementById('root');
ReactDOM.createRoot(rootEl).render(<Boot />);
